import { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { originStateFrom } from '@/shared/lib/origin';
import { Button, Dialog, Icons } from '@/shared/ui';

import {
  type Destination,
  isCurrent,
  KIND_FILTERS,
  type KindFilter,
  search,
  suggestions,
} from './destinations';
import styles from './QuickNavDialog.module.css';
import { useDestinations } from './use-destinations';

const ICONS = {
  workspace: <Icons.workspace />,
  section: <Icons.folder />,
  group: <Icons.group />,
  board: <Icons.board />,
  database: <Icons.viewTable />,
  table: <Icons.viewTable />,
  document: <Icons.document />,
} as const;

interface Props {
  onClose: () => void;
  /** Opens narrowed to one type (the board shortcut starts on boards). */
  initialKind?: KindFilter;
}

/**
 * The ONE resource switcher: the navbar button and Ctrl/Cmd+K both open this dialog. It lists
 * everything the viewer may open - workspaces, groups, boards, databases, notes, with their place -
 * narrowed by what is typed and, optionally, by a type chip. Empty text shows recently opened, then
 * favorites. Arrows move, Enter opens, Escape closes; the place the viewer is on is marked.
 */
export function QuickNavDialog({ onClose, initialKind = 'all' }: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [text, setText] = useState('');
  const [kind, setKind] = useState<KindFilter>(initialKind);
  const { destinations, pending, failed, retry } = useDestinations(text);
  const [active, setActive] = useState(0);
  const listId = useId();
  const list = useRef<HTMLUListElement>(null);
  const typed = text.trim() !== '';
  const results = useMemo(
    () => (typed ? search(destinations, text, 30, kind) : suggestions(destinations, 12, kind)),
    [destinations, text, kind, typed],
  );
  const current = Math.min(active, Math.max(results.length - 1, 0));
  // Without text the list is "recent" then "favorites": a heading opens each run.
  const headingAt = (index: number): string | null => {
    if (typed) return index === 0 ? t('quickNav.results') : null;
    const here = results[index];
    const before = results[index - 1];
    if (!here) return null;
    const recent = here.recent >= 0;
    if (index === 0 || (before && before.recent >= 0) !== recent) {
      return recent ? t('quickNav.recent') : t('quickNav.favorites');
    }
    return null;
  };

  const open = (destination: Destination) => {
    onClose();
    void navigate(destination.to, { state: originStateFrom(location) });
  };
  const move = (to: number) => {
    const next = Math.max(0, Math.min(to, results.length - 1));
    setActive(next);
    list.current?.querySelector(`[data-index="${String(next)}"]`)?.scrollIntoView({
      block: 'nearest',
    });
  };
  const pickKind = (value: KindFilter) => {
    setKind(value);
    setActive(0);
  };

  return (
    <Dialog title={t('quickNav.title')} onClose={onClose} size="lg" hideTitle>
      <div className={styles.body} data-testid="quick-nav">
        <div className={styles.field}>
          <Icons.search />
          <input
            type="text"
            role="combobox"
            aria-expanded
            aria-controls={listId}
            aria-activedescendant={results[current] ? `${listId}-${String(current)}` : undefined}
            aria-label={t('quickNav.label')}
            className={styles.input}
            placeholder={t('quickNav.placeholder')}
            value={text}
            autoFocus
            data-autofocus
            autoComplete="off"
            spellCheck={false}
            maxLength={200}
            onChange={(event) => {
              setText(event.target.value);
              setActive(0);
            }}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault();
                move(current + 1);
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                move(current - 1);
              } else if (event.key === 'Enter' && results[current]) {
                event.preventDefault();
                open(results[current]);
              }
            }}
          />
        </div>
        <div
          className={styles.kinds}
          role="radiogroup"
          aria-label={t('quickNav.type')}
          data-testid="quick-nav-kinds"
        >
          {KIND_FILTERS.map((value) => (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={kind === value}
              className={styles.kindChip}
              onClick={() => {
                pickKind(value);
              }}
            >
              {t(`quickNav.filters.${value}`)}
            </button>
          ))}
        </div>
        <ul
          id={listId}
          ref={list}
          role="listbox"
          className={styles.list}
          aria-label={t('quickNav.title')}
        >
          {results.map((destination, index) => {
            const heading = headingAt(index);
            const here = isCurrent(destination, location.pathname);
            return (
              <li key={destination.key} role="presentation" className={styles.entry}>
                {heading && <p className={styles.heading}>{heading}</p>}
                <div
                  id={`${listId}-${String(index)}`}
                  role="option"
                  aria-selected={index === current}
                  aria-current={here ? 'page' : undefined}
                  data-index={index}
                  data-kind={destination.kind}
                  className={styles.item}
                  data-active={index === current || undefined}
                  title={
                    destination.context
                      ? `${destination.name} — ${destination.context}`
                      : destination.name
                  }
                  onMouseMove={() => {
                    if (index !== current) setActive(index);
                  }}
                  onClick={() => {
                    open(destination);
                  }}
                >
                  <span className={styles.icon} aria-hidden>
                    {ICONS[destination.kind]}
                  </span>
                  <span className={styles.text}>
                    <span className={styles.name}>
                      {destination.favorite && (
                        <span className={styles.star} aria-hidden>
                          ★
                        </span>
                      )}
                      {destination.name}
                    </span>
                    {destination.context && (
                      <span className={styles.context}>{destination.context}</span>
                    )}
                  </span>
                  {here && <span className={styles.here}>{t('quickNav.current')}</span>}
                  <span className={styles.kind}>{t(`quickNav.kinds.${destination.kind}`)}</span>
                </div>
              </li>
            );
          })}
        </ul>
        {results.length === 0 && (
          <div className={styles.empty} role="status">
            {failed ? (
              <>
                <p>{t('quickNav.error')}</p>
                <Button size="sm" onClick={retry}>
                  {t('common.retry')}
                </Button>
              </>
            ) : pending ? (
              <p>{t('common.loading')}</p>
            ) : typed ? (
              <p>{t('quickNav.none', { query: text.trim() })}</p>
            ) : (
              <p>{t('quickNav.hint')}</p>
            )}
          </div>
        )}
        <p className={styles.footer}>{t('quickNav.keys')}</p>
      </div>
    </Dialog>
  );
}
